import { Checkbox } from "@mui/material";
import { useExceptions } from "~/components/exceptions/use-exceptions";
import { useFetchExceptions } from "~/components/exceptions/use-fetch-exceptions";
import {
  ExceptionType,
  GetAdvisoriesDocument,
  GetAssetInsightsDocument,
  GetChecksDocument,
  GetCvEsDocument,
  GetFindingsDocument,
  LoadAssetDocument,
} from "~/operations";
import { SelectedEntityWithException } from "~/components/exceptions/types";
import {
  FindingContext,
  findingsTableRowHrefBuilder,
  findingTableRowExceptionHrefBuilder,
  getFindingTableHeaders,
  sortFindingsToExceptionEntitiesMrns,
} from "~/pages/inventory/components/Findings/FindingsTable/utils/findingTableUtils";
import { ScopeType, useScope } from "~/hooks/useScope";
import { useSearchParams } from "react-router-dom";
import { getExceptionLink } from "~/pages/findings/Exceptions/utils";
import { FindingsTableProps } from "../FindingsTable";
import {
  SelectedEntityWithFindingData,
  SetExceptionDialog,
  SetExceptionDialogPayload,
} from "~/components/exceptions/SetExceptionDialog";
import { RemoveExceptionsGroupDialog } from "~/components/exceptions/RemoveExceptionsGroupDialog";
import { useDialogs } from "@toolpad/core";
import { useCanApplyExceptionMutation } from "~/lib/iam/actions/mondoo/policy/ExtendedHub/ApplyExceptionMutation";
import { useEffect, useMemo } from "react";
import { useTriageToolbar } from "~/providers/triage-toolbar";
import { useExceptionsButtons } from "~/components/exceptions/use-exceptions-buttons";
import { TriageToolbarButtonType } from "~/components/TriageToolbar/types";
import { useSelection } from "~/hooks/useSelection";
import { AdvisoryFindingsNode } from "~/pages/inventory/components/Findings/FindingsTable/utils/advisoryTypes";
import { CVEFindingsNode } from "~/pages/inventory/components/AssetSoftwareDetails/AssetSoftwareVulnerabilities/hooks/types";
import {
  CheckFindingsCheckNode,
  GenericFindingsNode,
} from "~/pages/inventory/asset/types";
import { Header } from "~/types/table";
import keyBy from "lodash-es/keyBy";
import { useSecurityPipelineIntegrations } from "~/pages/integrations/security-pipeline/hooks/useSecurityPipelineIntegrations";
import { CreateSecurityPipelinePrDialog } from "~/pages/integrations/security-pipeline/components/CreatePullRequest/CreateSecurityPipelinePRDialog";

export type AssetVulnerabilitiesTableHeaders = {
  SELECT?: Header;
  RISK_VALUE: Header;
  ADVISORY_ID?: Header;
  VULNERABILITY_ID?: Header;
  AllSecurityFinding?: Header;
  CHECK_ID?: Header;
  RISKFACTORS?: Header;
  TRIAGE_BUTTON?: Header;
};

type UseAssetVulnerabilitiesTableProps = {
  context: FindingContext;
  entityMrn: string;
  items: FindingsTableProps["findings"];
  assetId?: string;
  assetName?: string;
  assetMrn?: string;
  exceptionModalShouldListEntities?: boolean;
  totalCount?: number;
  canSelect?: boolean;
  showAction?: boolean;
};

export function useFindingsTable({
  context,
  entityMrn,
  items,
  assetId,
  assetName,
  assetMrn,
  exceptionModalShouldListEntities,
  totalCount,
  canSelect = true,
  showAction = true,
}: UseAssetVulnerabilitiesTableProps) {
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get("jobId") || "";
  const projectId = searchParams.get("projectId") || "";
  const { activeScope: scope, spaceScope } = useScope();
  const dialogs = useDialogs();
  const triageToolbar = useTriageToolbar();
  const hasApplyExceptionPermission = useCanApplyExceptionMutation(spaceScope);
  const findingsByMrn = keyBy(
    items.filter(
      (item): item is CVEFindingsNode | AdvisoryFindingsNode =>
        item.__typename === "CveFinding" ||
        item.__typename === "AdvisoryFinding",
    ),
    "mrn",
  );
  const { hasSecurityPipelineIntegrations } = useSecurityPipelineIntegrations();

  const {
    isGroupChecked,
    isGroupIndeterminate,
    onGroupCheckChange,
    selectedEntities,
    setSelectedEntities,
    handleNodeClick,
    handleNodeChange,
    handleCancelClick,
  } = useSelection<
    SelectedEntityWithFindingData & SelectedEntityWithException
  >();

  const {
    exceptionsButtonsAvailability,
    exceptionsButtonsVisibility,
    exceptionsButtonsTooltipText,
  } = useExceptionsButtons({
    selectedEntities,
    target: "finding",
    showExceptionButtons: true,
    showScopeButtons: false,
  });

  useEffect(() => {
    if (selectedEntities.length > 0) {
      triageToolbar.show({
        buttons: [
          ...(exceptionsButtonsVisibility.SET_EXCEPTION
            ? [
                {
                  type: TriageToolbarButtonType.CreateException,
                  onClick: handleSetExceptionModalOpen,
                  disabled: !exceptionsButtonsAvailability.SET_EXCEPTION,
                  tooltip: exceptionsButtonsTooltipText.SET_EXCEPTION,
                },
              ]
            : []),
          ...(exceptionsButtonsVisibility.REMOVE_EXCEPTION
            ? [
                {
                  type: TriageToolbarButtonType.RemoveException,
                  onClick: handleRemoveExceptionModalOpen,
                  disabled: !exceptionsButtonsAvailability.REMOVE_EXCEPTION,
                  tooltip: exceptionsButtonsTooltipText.REMOVE_EXCEPTION,
                },
              ]
            : []),
          ...(scope?.type === ScopeType.Space && hasSecurityPipelineIntegrations
            ? [
                {
                  type: TriageToolbarButtonType.CreateRemediationWorkflow,
                  onClick: handleCreateRemediationPRDialogOpen,
                  disabled: selectedEntities.some((e) => {
                    return !["CveFinding", "AdvisoryFinding"].includes(
                      e.type || "",
                    );
                  }),
                  tooltip:
                    "Mondoo cannot create workflows for check and generic findings",
                },
              ]
            : []),
        ],
        payload: {
          count: selectedEntities.length,
          totalCount,
          onClose: handleCancelClick,
        },
      });
    } else {
      triageToolbar.hide();
    }

    return () => {
      triageToolbar.hide();
    };
  }, [selectedEntities]);

  const {
    handleRemoveExceptionGroups,
    handleSetException,
    loading: exceptionsLoading,
  } = useExceptions({
    onSetException: async () => {
      setSelectedEntities([]);
    },
    scopeMrns: [entityMrn],
    ...sortFindingsToExceptionEntitiesMrns({ selectedEntities }),
    refetchQueries: [
      context === "cve" || context === "critical_exposure"
        ? GetCvEsDocument
        : GetAdvisoriesDocument,
      ...(context === "check" ? [GetChecksDocument] : []),
      // we need to refetch GetAssetInsightsDocument of asset score breakdown data in asset page
      ...(context === "all"
        ? [GetFindingsDocument, GetAssetInsightsDocument]
        : []),
      LoadAssetDocument,
    ],
  });

  const handleSetExceptionModalOpen = async () => {
    const exceptionDialogPayload: SetExceptionDialogPayload = {
      target: "asset",
      selectedEntities: selectedEntities,
      onSave: handleSetException,
      shouldListEntities: exceptionModalShouldListEntities,
      targetData: {
        targetTitle: assetName,
        onAsset: {
          id: assetId || "",
          mrn: assetMrn || "",
          name: assetName || "",
          __typename: "EntityInfoAsset",
        },
      },
    };

    dialogs.open(SetExceptionDialog, exceptionDialogPayload);
  };

  const handleRemoveExceptionModalOpen = () => {
    dialogs.open(RemoveExceptionsGroupDialog, {
      onRemove: async (params) => {
        await handleRemoveExceptionGroups(params);
        setSelectedEntities([]);
      },
      exceptionMrns: exceptionGroups.map((eg) => eg.mrn),
    });
  };

  const handleCreateRemediationPRDialogOpen = () => {
    if (!spaceScope || !scope) return;

    const findings = selectedEntities.map(
      (entity) => findingsByMrn[entity.mrn],
    );

    dialogs.open(CreateSecurityPipelinePrDialog, {
      spaceScope: spaceScope,
      findings: findings,
      ...(assetMrn ? { assetMrns: [assetMrn] } : {}),
    });
  };

  const { exceptionGroups } = useFetchExceptions({
    scopeMrn: entityMrn,
    skip: !entityMrn,
    types:
      context === "advisory" ? [ExceptionType.Advisory] : [ExceptionType.Cve],
  });

  const targetPaginatedGroup = items.map((item) => {
    return {
      mrn: item.mrn as string,
      exception:
        item.__typename !== "GenericFinding" ? item.exception : undefined,
      groupId:
        item.__typename !== "GenericFinding" ? item.exception?.id : undefined,
      title: item.title,
      rating: item.rating,
      type: item.__typename,
      data: {
        id: item.id,
        title: item.title,
        rating: item.rating,
      },
    };
  });

  const assetVulnerabilitiesTableHeaders: AssetVulnerabilitiesTableHeaders = {
    SELECT: {
      id: "SELECT",
      label: (
        <Checkbox
          checked={isGroupChecked(targetPaginatedGroup)}
          indeterminate={isGroupIndeterminate(targetPaginatedGroup)}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            onGroupCheckChange(event, targetPaginatedGroup)
          }
        />
      ),
      sortable: false,
    },
    RISK_VALUE: {
      id: "RISK_VALUE",
      label: "Risk",
      sortable: true,
      options: { width: "10%" },
    },
    ADVISORY_ID: {
      id: "ADVISORY_ID",
      label: "Advisory",
      options: { width: context === "advisory" ? "75%" : "50%" },
      sortable: false,
    },
    VULNERABILITY_ID: {
      id: "VULNERABILITY_ID",
      label: "Vulnerability",
      options: { width: context === "cve" ? "75%" : "50%" },
      sortable: false,
    },
    AllSecurityFinding: {
      id: "ALL_SECURITY_FINDING",
      label: "Finding",
      sortable: false,
      options: { width: "55%" },
    },
    CHECK_ID: {
      id: "CHECK_ID",
      label: "Check",
      sortable: false,
    },
    RISKFACTORS: {
      id: "RISKFACTORS",
      label: "Risk factors",
      options: { width: "15%" },
      sortable: false,
    },
    TRIAGE_BUTTON: {
      id: "TRIAGE_BUTTON",
      label: "Action",
      sortable: false,
    },
  };

  const tableHeaders = getFindingTableHeaders(
    context,
    hasApplyExceptionPermission && canSelect,
    assetVulnerabilitiesTableHeaders,
    showAction,
  );

  const getFindingsTableRowHref = ({
    context,
    assetId,
    finding,
  }: {
    context: FindingContext;
    assetId: string | undefined;
    finding:
      | CVEFindingsNode
      | AdvisoryFindingsNode
      | CheckFindingsCheckNode
      | GenericFindingsNode;
  }) => {
    return findingsTableRowHrefBuilder({
      context,
      assetId,
      jobId,
      projectId,
      scopeParams: scope?.params,
      finding,
    });
  };

  function getFindingTableRowExceptionHref({
    assetId,
    exception,
  }: {
    assetId: string | undefined;
    exception:
      | {
          id: string;
          exceptionId: string;
          scope: string;
        }
      | null
      | undefined;
  }) {
    return exception?.scope.includes(assetId || "")
      ? findingTableRowExceptionHrefBuilder({
          exceptionId: exception.id,
          id: exception?.exceptionId,
          assetId,
          scopeId: scope?.id,
          context,
        })
      : getExceptionLink({
          id: exception?.exceptionId || "",
          queryParams: new URLSearchParams(),
        });
  }

  return {
    hasApplyExceptionPermission,
    tableHeaders,
    getFindingsTableRowHref,
    getFindingTableRowExceptionHref,
    targetPaginatedGroup,
    jobId,
    projectId,
    exception: {
      handleRemoveExceptionGroups,
      handleSetException,
      handleSetExceptionModalOpen,
      handleRemoveExceptionModalOpen,
      loading: exceptionsLoading,
    },
    exceptionsSelection: {
      isGroupChecked,
      isGroupIndeterminate,
      onGroupCheckChange,
      selectedEntities,
      setSelectedEntities,
      handleNodeClick,
      handleNodeChange,
      handleCancelClick,
    },
  };
}
